/**
 * Composable for fetching programs from LARX API
 *
 * Used for LARX-based tenants to fetch programs from the destination university.
 */

import { ref, type Ref } from 'vue'
import { degreeReqsApi, type ApiProgram } from '@/services/degreeReqsApi'
import { getConfig, usesLarxDataSource } from '@/config'

interface UseLarxProgramsReturn {
  /** List of available programs */
  programs: Ref<ApiProgram[]>
  /** Loading state */
  loading: Ref<boolean>
  /** Error message if any */
  error: Ref<string | null>
  /** Fetch programs for the configured destination university */
  fetchPrograms: () => Promise<void>
  /** Get program requirements by ID */
  getRequirements: (programId: number) => Promise<any>
}

/**
 * Fetch and manage programs from LARX API
 *
 * @example
 * ```ts
 * const { programs, loading, error, fetchPrograms, getRequirements } = useLarxPrograms()
 *
 * onMounted(async () => {
 *   await fetchPrograms()
 * })
 *
 * // Get requirements for selected program
 * const reqs = await getRequirements(selectedProgram.id)
 * ```
 */
export function useLarxPrograms(): UseLarxProgramsReturn {
  const programs = ref<ApiProgram[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch programs from LARX for the destination university
   */
  async function fetchPrograms(): Promise<void> {
    if (!usesLarxDataSource()) {
      error.value = 'LARX is not the data source for this tenant'
      return
    }

    loading.value = true
    error.value = null

    try {
      const config = getConfig()
      const { destination } = config.institutions

      // Resolve university ID by name
      const universityId = await degreeReqsApi.findUniversityByName(destination.name)

      if (!universityId) {
        throw new Error(`Could not find university: ${destination.name}`)
      }

      // Fetch programs
      const fetchedPrograms = await degreeReqsApi.getUniversityPrograms(universityId)
      programs.value = fetchedPrograms
    } catch (err) {
      console.error('Error fetching programs:', err)
      error.value = err instanceof Error ? err.message : 'Failed to fetch programs'
    } finally {
      loading.value = false
    }
  }

  /**
   * Get requirements for a specific program
   */
  async function getRequirements(programId: number): Promise<any> {
    if (!usesLarxDataSource()) {
      throw new Error('LARX is not the data source for this tenant')
    }

    return degreeReqsApi.getProgramRequirements(programId)
  }

  return {
    programs,
    loading,
    error,
    fetchPrograms,
    getRequirements,
  }
}
